// Hand-written: the news and blog listings.
//
// Figma 6377:88447 (News Section) and 6377:88468 (Blog Section) - the Aktualnosci page
// is **two** of these sections, not one. They sit either side of a grey `Rectangle 73`
// separator strip in the file, which is the convention CLAUDE.md warns about: a strip
// means two catalogue entries, not one design.
//
// Layout, measured from the file rather than guessed:
//
// - The head is two 664px columns on a 40px gutter: the title at `Display-01` (82px) on
//   the left, the lead at `body-medium` on the right. Not the stacked heading-then-lead
//   every other section uses.
// - The cards are a **four-column grid on a 16px gutter** (4 x 330 + 3 x 16 = 1368) in
//   which **the first card spans two columns** (676 = 330 + 16 + 330). News draws seven
//   cards over two rows, blog draws three over one; that is only `pageSize`.
// - Below the grid, 36px down, a "Sprawdz wiecej" link: 20px black text with a 1px rule
//   9px under it. Not the blue arrow the other sections use, and not numbered pages.
//
// Two things the design does not show, kept because they were built for M7 and a real
// archive needs them: the category filter bar (`showFilters`, off by default) and
// numbered pagination (`pagination: 'pages'`). The design's link is `pagination: 'more'`,
// which appends the next batch rather than navigating.
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SectionShell } from '../primitives';
import ArticleCard from '../../components/ArticleCard';
import { api, type ApiArticle, type ApiArticleCategory, type ApiListMeta } from '../../lib/api';
import { useSite } from '../../lib/site-context';
import type { ArticleGridSection } from '../../types/sections';
import { AppLink } from '../../components/ui/AppLink';

const EMPTY_META: ApiListMeta = { page: 1, pageSize: 0, total: 0, pageCount: 1 };

/** The page numbers to draw: all of them, or a window of five around the current one. */
function pageWindow(current: number, pageCount: number): number[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const start = Math.min(Math.max(current - 2, 1), pageCount - 4);
  return Array.from({ length: 5 }, (_, i) => start + i);
}

const CHIP = 'rounded-full px-4 py-2 text-body-sm transition';
const CHIP_ON = 'bg-brand-500 text-white';
const CHIP_OFF = 'bg-surface-muted text-ink hover:bg-surface-sunken';

/** Figma 6377:88464 - 20px black, a 1px rule 9px below, only as wide as the text. */
const SEE_MORE = 'w-fit border-b border-ink-strong pb-[9px] text-h5 text-ink-strong transition hover:opacity-70';

/**
 * One page of articles, reloaded whenever the question changes.
 *
 * A hook rather than an effect in the component, because the three pieces of state it
 * owns - the articles, the pagination meta and whether a request is in flight - are only
 * ever written together and only ever by this request. Inline they were three `useState`
 * calls a reader had to check nothing else touched.
 *
 * `cancelled` is the usual guard: a filter changed quickly enough leaves two requests in
 * flight, and the slower one must not overwrite the newer answer.
 */
function useArticleList(
  regionCode: string,
  locale: string,
  query: { pageSize: number; page: number; kind: NonNullable<ArticleGridSection['kind']>; category: string }
) {
  const [items, setItems] = useState<ApiArticle[]>([]);
  const [meta, setMeta] = useState<ApiListMeta>(EMPTY_META);
  const [loading, setLoading] = useState(true);

  const { pageSize, page, kind, category } = query;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    api.articleList(regionCode, locale, { pageSize, page, kind, category: category || undefined })
      .then((res) => { if (!cancelled) { setItems(res.items); setMeta(res.meta); } })
      .catch(() => { if (!cancelled) { setItems([]); setMeta(EMPTY_META); } })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [regionCode, locale, pageSize, page, kind, category]);

  return { items, meta, loading };
}

export default function ArticleGrid({ data, first = false }: { data: ArticleGridSection; first?: boolean }) {
  const { region, locale, articlePath, t } = useSite();
  const [params, setParams] = useSearchParams();

  const category = params.get('category') ?? '';
  const page = Math.max(Number.parseInt(params.get('page') ?? '1', 10) || 1, 1);

  const [categories, setCategories] = useState<ApiArticleCategory[]>([]);
  // How many batches the "see more" link has asked for. Kept out of the URL: a reader
  // who has pressed it three times is not at a different page, they are further down
  // the same one.
  const [batches, setBatches] = useState(1);

  const pageSize = data.pageSize ?? 12;
  const kind = data.kind ?? 'all';
  const mode = data.pagination ?? 'more';
  const linksAway = Boolean(data.cta?.url);

  // In "more" mode one request fetches every batch asked for so far, rather than
  // stitching pages together in the client. Slightly more bytes on the second press,
  // and no chance of a duplicate or a hole when an editor publishes mid-scroll.
  const wanted = mode === 'more' ? pageSize * batches : pageSize;
  const requestedPage = mode === 'pages' ? page : 1;

  useEffect(() => { setBatches(1); }, [category, kind, pageSize]);

  const { items, meta, loading } = useArticleList(region.code, locale, {
    pageSize: wanted, page: requestedPage, kind, category
  });

  // The filter list comes from the CMS, not from whatever happens to be on this page.
  // Deriving it from the loaded articles - which is what this section used to do - hid
  // every category that had no article on page one.
  useEffect(() => {
    if (!data.showFilters) return;
    let cancelled = false;
    api.articleCategories(region.code, locale)
      .then((res) => { if (!cancelled) setCategories(res.filter((c) => c.count > 0)); })
      .catch(() => { if (!cancelled) setCategories([]); });
    return () => { cancelled = true; };
  }, [region.code, locale, data.showFilters]);

  /** Write the filter and the page back into the URL, dropping the defaults. */
  const navigate = (next: { category?: string; page?: number }) => {
    const updated = new URLSearchParams(params);
    if (next.category !== undefined) {
      if (next.category) updated.set('category', next.category);
      else updated.delete('category');
      updated.delete('page'); // a new filter starts at the beginning
    }
    if (next.page !== undefined) {
      if (next.page > 1) updated.set('page', String(next.page));
      else updated.delete('page');
    }
    setParams(updated, { replace: false });
  };

  const numbers = useMemo(() => pageWindow(meta.page, meta.pageCount), [meta.page, meta.pageCount]);
  // A category that has since been emptied would otherwise vanish from the bar while
  // still filtering the list, leaving no way back except editing the URL.
  const showUnknownCategory = Boolean(category) && !categories.some((c) => c.slug === category);
  const hasMore = items.length < meta.total;
  const Title = first ? 'h1' : 'h2';

  return (
    <SectionShell settings={data.settings} name="article-grid">
      <div className="flex flex-col">
        {/* Head: title and lead side by side, two equal columns on a 40px gutter. */}
        {data.heading || data.lead ? (
          <div className="grid gap-6 lg:grid-cols-2 lg:gap-10">
            {data.heading ? (
              <Title className="text-h2 font-normal text-ink-strong lg:text-display">{data.heading}</Title>
            ) : <div />}
            {data.lead ? <p className="whitespace-pre-line text-body-md text-ink">{data.lead}</p> : null}
          </div>
        ) : null}

        {data.showFilters && (categories.length > 0 || showUnknownCategory) ? (
          <div className="mt-10 flex flex-wrap gap-2" role="group" aria-label={t('news.categories')}>
            <button
              type="button"
              onClick={() => navigate({ category: '' })}
              aria-pressed={category === ''}
              className={`${CHIP} ${category === '' ? CHIP_ON : CHIP_OFF}`}
            >
              {t('news.allCategories')}
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => navigate({ category: c.slug })}
                aria-pressed={category === c.slug}
                className={`${CHIP} ${category === c.slug ? CHIP_ON : CHIP_OFF}`}
              >
                {c.name}
              </button>
            ))}
            {showUnknownCategory ? (
              <button type="button" aria-pressed className={`${CHIP} ${CHIP_ON}`} onClick={() => navigate({ category: '' })}>
                {category}
              </button>
            ) : null}
          </div>
        ) : null}

        {/* 120px below the head in the design; less on a narrow screen, where 120px of
            nothing is most of the viewport. */}
        <div className="mt-12 lg:mt-[120px]">
          {loading && !items.length ? (
            <p className="text-body-sm text-ink-faint">{t('page.loading')}</p>
          ) : items.length ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {items.map((article, i) => (
                <ArticleCard
                  key={article.slug}
                  article={article}
                  href={articlePath(article.slug)}
                  // The first card is double-width, and stays double-width as more are
                  // loaded - it is the position that is featured, not the article.
                  size={i === 0 ? 'half' : 'quarter'}
                />
              ))}
            </div>
          ) : (
            <p className="text-body-sm text-ink-faint">{t('news.empty')}</p>
          )}
        </div>

        {mode === 'more' && (linksAway || hasMore) ? (
          <div className="mt-9">
            {linksAway ? (
              <AppLink to={data.cta?.url} className={SEE_MORE}>{data.cta?.label || t('news.all')}</AppLink>
            ) : (
              <button type="button" onClick={() => setBatches((b) => b + 1)} disabled={loading} className={SEE_MORE}>
                {loading ? t('page.loading') : (data.cta?.label || t('news.all'))}
              </button>
            )}
          </div>
        ) : null}

        {mode === 'pages' && meta.pageCount > 1 ? (
          <nav aria-label={t('news.pagination')} className="mt-9 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => navigate({ page: meta.page - 1 })}
              disabled={meta.page <= 1}
              className={`${CHIP} ${CHIP_OFF} disabled:cursor-not-allowed disabled:opacity-40`}
            >
              {t('news.previous')}
            </button>

            {numbers.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => navigate({ page: n })}
                aria-current={n === meta.page ? 'page' : undefined}
                aria-label={`${t('news.page')} ${n}`}
                className={`${CHIP} ${n === meta.page ? CHIP_ON : CHIP_OFF}`}
              >
                {n}
              </button>
            ))}

            <button
              type="button"
              onClick={() => navigate({ page: meta.page + 1 })}
              disabled={meta.page >= meta.pageCount}
              className={`${CHIP} ${CHIP_OFF} disabled:cursor-not-allowed disabled:opacity-40`}
            >
              {t('news.next')}
            </button>

            <span className="pl-2 text-body-sm text-ink-faint">
              {t('news.pageOf', { page: meta.page, pages: meta.pageCount })}
            </span>
          </nav>
        ) : null}
      </div>
    </SectionShell>
  );
}
